import { useQuery } from '@tanstack/react-query'
import { queryKeys } from './queryKeys'
import {
   TermLengthType,
   PrepayLengthType,
   Opportunity,
   AccountSnowflakeData,
} from '../../types/types'

const host_url = process.env.REACT_APP_HOST_URL

/**
 * Fetches the opportunity data from the API
 *
 * @param {string} opportunity_id - The ID of the opportunity to fetch
 * @returns {Opportunity} The opportunity data
 */
function useGetOpportunity(opportunity_id: string) {
   return useQuery<Opportunity>({
      queryKey: queryKeys.getOpportunity,
      queryFn: async () => {
         if (!host_url || !opportunity_id) return {}
         const response = await fetch(
            `${host_url}/api/get-opp?opportunity_id=${opportunity_id}`,
         )
         return response.json() || {}
      },
      enabled: !!host_url && !!opportunity_id,
   })
}

/**
 * Fetches the pricebooks from the API
 *
 * @param {string} opportunity_id - The ID of the opportunity to fetch
 * @param {string} opportunity_created_date - The created date of the opportunity
 * @returns {Pricebook[]} The pricebooks
 */
function useGetPricebooks(
   opportunity_id: string,
   opportunity_created_date: string,
) {
   return useQuery({
      queryKey: queryKeys.pricebooks,
      queryFn: async () => {
         if (!host_url) return {}
         const response = await fetch(
            `${host_url}/api/pricebooks?opportunity_id=${opportunity_id}&opportunity_created_date=${Date.parse(opportunity_created_date)}`,
         )
         return response.json() || {}
      },
      enabled: !!host_url,
   })
}

/**
 * Fetches the currency data from the API
 *
 * @param {string} currencyCode - The currency code to fetch
 * @returns {Currency} The currency data
 */
function useGetCurrency(currencyCode: string) {
   return useQuery({
      queryKey: queryKeys.currency(currencyCode),
      queryFn: async () => {
         if (!host_url) return {}
         const response = await fetch(
            `${host_url}/api/currency?currencyCode=${currencyCode}`,
         )
         return response.json() || {}
      },
      enabled: !!host_url && !!currencyCode,
   })
}

/**
 * Fetches the products from the API
 *
 * @param {string} pricebookId - The ID of the pricebook
 * @param {string} currencyCode - The currency code
 * @param {string} user_role - The user role
 * @returns {Object} The products data
 */
function useGetProducts(
   pricebookId: string,
   currencyCode: string,
   user_role: string,
) {
   return useQuery({
      queryKey: queryKeys.products(pricebookId, currencyCode),
      queryFn: async () => {
         if (!host_url) return {}
         const response = await fetch(
            `${host_url}/api/products?book_id=${pricebookId}&currencyCode=${currencyCode}&user_role=${encodeURIComponent(user_role)}`,
         )
         const data = (await response.json()) || {}

         // Convert ShowOnScreen arrays to Set objects
         Object.keys(data).forEach((key) => {
            if (
               data[key] &&
               typeof data[key] === 'object' &&
               data[key].ShowOnScreen &&
               Array.isArray(data[key].ShowOnScreen)
            ) {
               data[key].ShowOnScreen = new Set(data[key].ShowOnScreen)
            }
         })

         // Convert UseHeadcountOf arrays to Set objects (only for product entries)
         Object.keys(data).forEach((key) => {
            if (
               data[key] &&
               typeof data[key] === 'object' &&
               data[key].ProductCode
            ) {
               if (Array.isArray(data[key].UseHeadcountOf)) {
                  data[key].UseHeadcountOf = new Set(data[key].UseHeadcountOf)
               } else {
                  data[key].UseHeadcountOf = new Set()
               }
            }
         })

         // Convert OverrideRoles arrays to Set objects (only for product entries)
         Object.keys(data).forEach((key) => {
            if (
               data[key] &&
               typeof data[key] === 'object' &&
               data[key].ProductCode
            ) {
               if (Array.isArray(data[key].OverrideRoles)) {
                  data[key].OverrideRoles = new Set(data[key].OverrideRoles)
               } else {
                  data[key].OverrideRoles = new Set()
               }
            }
         })

         return data
      },
      enabled: !!host_url && !!pricebookId && !!currencyCode && !!user_role,
   })
}

/**
 * Fetches the previous pricebook products from the API
 *
 * @param {string} prevPricebookId - The ID of the previous pricebook
 * @param {string} currencyCode - The currency code
 * @returns {Object} The previous products data
 */
function useGetPrevProducts(prevPricebookId: string, currencyCode: string) {
   return useQuery({
      queryKey: queryKeys.prevProducts(prevPricebookId, currencyCode),
      queryFn: async () => {
         if (!host_url) return {}
         const response = await fetch(
            `${host_url}/api/products?book_id=${prevPricebookId}&currencyCode=${currencyCode}`,
         )
         const data = (await response.json()) || {}

         // Convert ShowOnScreen arrays to Set objects
         Object.keys(data).forEach((key) => {
            if (
               data[key] &&
               typeof data[key] === 'object' &&
               data[key].ShowOnScreen &&
               Array.isArray(data[key].ShowOnScreen)
            ) {
               data[key].ShowOnScreen = new Set(data[key].ShowOnScreen)
            }
         })

         // Convert UseHeadcountOf arrays to Set objects (only for product entries)
         Object.keys(data).forEach((key) => {
            if (
               data[key] &&
               typeof data[key] === 'object' &&
               data[key].ProductCode
            ) {
               if (Array.isArray(data[key].UseHeadcountOf)) {
                  data[key].UseHeadcountOf = new Set(data[key].UseHeadcountOf)
               } else {
                  data[key].UseHeadcountOf = new Set()
               }
            }
         })

         // Convert OverrideRoles arrays to Set objects (only for product entries)
         Object.keys(data).forEach((key) => {
            if (
               data[key] &&
               typeof data[key] === 'object' &&
               data[key].ProductCode
            ) {
               if (Array.isArray(data[key].OverrideRoles)) {
                  data[key].OverrideRoles = new Set(data[key].OverrideRoles)
               } else {
                  data[key].OverrideRoles = new Set()
               }
            }
         })

         return data
      },
      enabled: !!host_url && !!prevPricebookId && !!currencyCode,
   })
}

/**
 * Fetches the discounts from the API
 *
 * @param {string} pricebookId - The ID of the pricebook
 * @param {string} currencyCode - The currency code
 * @param {string} promoUTM - The promo UTM parameter
 * @param {string} accountFlags - The account flags
 * @param {string} opportunityType - The opportunity type
 * @param {string} accountSnowflake - The account snowflake data
 * @param {string} partnerAccount - The partner account
 * @param {string} opportunityId - The opportunity ID
 * @param {string} userRole - The user role
 * @param {number} discountDealDeskHRImpRate - The dealdesk HR implementation rate
 * @param {number} discountDealDeskQuickstartImpRate - The dealdesk quickstart rate
 * @param {number} discountDealDeskPayrollImpRate - The dealdesk payroll rate
 * @param {number} discountReseller - The reseller discount
 * @returns {Object} The discounts data
 */
function useGetDiscounts(
   pricebookId: string,
   currencyCode: string,
   promoUTM: string,
   accountFlags: string,
   opportunityType: string,
   accountSnowflake: string,
   partnerAccount: string,
   region: string,
   opportunityId: string,
   userRole: string,
   discountDealDeskHRImpRate: number | null,
   discountDealDeskQuickstartImpRate: number | null,
   discountDealDeskPayrollImpRate: number | null,
   discountReseller: number | null,
) {
   return useQuery({
      queryKey: queryKeys.discounts(
         pricebookId,
         currencyCode,
         accountSnowflake,
      ),
      queryFn: async () => {
         if (!host_url) return {}
         const response = await fetch(
            `${host_url}/api/discounts?book_id=${pricebookId}&promo_utm=${encodeURIComponent(promoUTM || '')}&account_flags=${accountFlags}&currencyCode=${currencyCode}&opportunity_type=${opportunityType}&account_snowflake=${encodeURIComponent(accountSnowflake)}&partner_account=${partnerAccount}&region=${encodeURIComponent(region || '')}&opportunity_id=${opportunityId}&user_role=${encodeURIComponent(userRole)}`,
         )
         const data = (await response.json()) || {}

         // Sets new discount rates if dealdesk values have been set
         if (
            discountDealDeskHRImpRate != null &&
            discountDealDeskHRImpRate != 0
         ) {
            if ('hrDealdesk' in data)
               data['hrDealdesk']['DiscountAmount'] = discountDealDeskHRImpRate
            else
               console.error(
                  'error: DealDesk Imp Discount Amount provided, but hrDealdesk not in discount list',
               )
         }
         if (
            discountDealDeskQuickstartImpRate != null &&
            discountDealDeskQuickstartImpRate != 0
         ) {
            if ('quickstartDiscount' in data) {
               data['quickstartDiscount']['DiscountType'] = 'percent'
               data['quickstartDiscount']['DiscountAmount'] =
                  discountDealDeskQuickstartImpRate
            } else
               console.log(
                  'error: DealDesk Quickstart Discount Amount provided, but quickstartDiscount not in discount list',
               )
         }
         if (
            discountDealDeskPayrollImpRate != null &&
            discountDealDeskPayrollImpRate != 0
         ) {
            if ('payDealdesk' in data)
               data['payDealdesk']['DiscountAmount'] =
                  discountDealDeskPayrollImpRate
            else
               console.log(
                  'error: DealDesk Payroll Imp Discount Amount provided, but payDealdesk not in discount list',
               )
         }
         // Sets new discount rate for resellers
         if (discountReseller != null) {
            if ('reseller' in data)
               data['reseller']['DiscountAmount'] = discountReseller
            else
               console.log(
                  'error: Reseller Discount Amount provided, but reseller not in discount list',
               )
         }

         return data
      },
      enabled: !!host_url && !!pricebookId && !!currencyCode && !!userRole,
   })
}

/**
 * Fetches the term lengths options from the API.
 * The backend looks up the opportunity's saved term_length to include it even if user doesn't have role access,
 * mirroring how discounts and products preserve admin-added items for reps who can't normally select them.
 *
 * @param {string} opportunity_id - The ID of the opportunity to fetch
 * @param {string} user_role - The user role
 * @returns {TermLengthType[]} The term lengths options
 */
function useTermLengths(opportunity_id: string, user_role: string) {
   return useQuery<TermLengthType[]>({
      queryKey: queryKeys.termLengths,
      queryFn: async () => {
         const response = await fetch(
            `${host_url}/api/term_lengths?opportunity_id=${opportunity_id}&user_role=${user_role}`,
         )
         return response.json() || []
      },
      enabled: !!host_url,
   })
}

/**
 * Fetches the prepay lengths options from the API
 * NEW: Prepay length is decoupled from term length for flexible prepayment models
 * The backend looks up the opportunity's saved prepay_length to include it even if user doesn't have role access
 *
 * @param {string} opportunity_id - The ID of the opportunity to fetch
 * @param {string} user_role - The user role
 * @returns {PrepayLengthType[]} The prepay lengths options
 */
function usePrepayLengths(opportunity_id: string, user_role: string) {
   return useQuery<PrepayLengthType[]>({
      queryKey: queryKeys.prepayLengths,
      queryFn: async () => {
         const response = await fetch(
            `${host_url}/api/prepay_lengths?opportunity_id=${opportunity_id}&user_role=${user_role}`,
         )
         return response.json() || []
      },
      enabled: !!host_url && !!opportunity_id,
   })
}

/**
 * Fetches pricing adjustments from the API based on a customer's existing pricing
 * from AccountSnowflakeData. The server computes a percentage discount that brings
 * each product's list price down to the customer's existing price.
 *
 * React Query handles caching and automatic cancellation of stale requests,
 * eliminating race conditions from rapid pricebook switches.
 *
 * @param {string} bookId - The pricebook ID
 * @param {string} currencyCode - The currency code for the current quote
 * @param {AccountSnowflakeData} accountSnowflake - The account snowflake data object
 * @returns {{ numberDiscountValues: Record<string, number>, discountStates: Record<string, boolean | string> }}
 */
type PricingAdjustmentsResult = {
   numberDiscountValues: Record<string, number>
   discountStates: Record<string, boolean | string>
}

function useGetPricingAdjustments(
   bookId: string,
   currencyCode: string,
   accountSnowflake: AccountSnowflakeData,
   isLoaded: boolean = false,
) {
   // Use sorted keys for stable query key regardless of property insertion order
   const accountSnowflakeKey = JSON.stringify(
      accountSnowflake,
      Object.keys(accountSnowflake).sort(),
   )
   return useQuery<PricingAdjustmentsResult>({
      queryKey: queryKeys.pricingAdjustments(
         bookId,
         currencyCode,
         accountSnowflakeKey,
      ),
      queryFn: async () => {
         if (!host_url) return { numberDiscountValues: {}, discountStates: {} }
         const response = await fetch(`${host_url}/api/pricing-adjustments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
               account_snowflake: accountSnowflake,
               book_id: bookId,
               currency_code: currencyCode,
            }),
         })
         if (!response.ok) {
            console.error(
               '[PricingAdj] API error:',
               response.status,
               response.statusText,
            )
            return { numberDiscountValues: {}, discountStates: {} }
         }
         return response.json()
      },
      enabled:
         !!host_url &&
         !!bookId &&
         !!currencyCode &&
         isLoaded &&
         !!accountSnowflake &&
         Object.keys(accountSnowflake).length > 0,
   })
}

export {
   useTermLengths,
   usePrepayLengths,
   useGetOpportunity,
   useGetPricebooks,
   useGetCurrency,
   useGetProducts,
   useGetPrevProducts,
   useGetDiscounts,
   useGetPricingAdjustments,
}
